import _ from 'underscore';
import {
    computed, type Ref, ref, unref, watch,
} from 'vue';
import { useState } from 'vuex-composition-helpers';
import { collapseOperators, canonicalOperatorId, isAffiliateOperatorId } from '@entities/operator';
import { filterToProductType } from '@ve/services/productTypeConverter';
import { type MapForFiltersState } from '@ve/components/hotel/filters/common/store/types';
import { wordEnding } from '@ve/services/textModifiers';
import { type Operator } from '@ve/types';

const filtersMappingStoreName = 'mapForFilters';

export default ({
                    selectedOperators,
                }: {
    selectedOperators: Ref<number[]>;
}) => {
    const {
        products,
        productType,
    } = useState<MapForFiltersState>(filtersMappingStoreName, ['products', 'productType']);

    const productsForSelectedType = computed(() => {
        const selectedProductType = unref(productType);
        if (selectedProductType === 'all') return unref(products);
        return unref(products).filter((product) => product.type === filterToProductType(selectedProductType));
    });

    const currentOperatorsReference = computed(() => collapseOperators(_.uniq(unref(productsForSelectedType).map((product) => product.operator), (operator) => operator.id)).filter((operator) => !isAffiliateOperatorId(operator.id)));

    // каждый новый поиск приносит туры от операторов и справочник доступных операторов обновляется.
    // Если вдруг так получилось, что выбран фильтр по какому-то туроператору, который не пришел в новой выдаче, то этот оператор должен отобразиться выбранным из reserveOperators списка всех операторов, которые были получены за всю историю работы страницы, чтобы его можно было отключить. Никакие фильтры автоматически не сбрасываются
    const reservedOperators: Ref<Operator[]> = ref([]);
    watch(currentOperatorsReference, (operatorsReference) => {
        reservedOperators.value = _.uniq(([] as Operator[]).concat(unref(reservedOperators), operatorsReference), (opeartor) => opeartor.id);
    });
    const reservedOperatorsReference = computed(() => {
        const operatorsValue = unref(selectedOperators);
        if (operatorsValue.length === 0) return [];

        const reservedOperatorsReferenceValue: Operator[] = [];
        unref(selectedOperators).forEach((operatorId) => {
            if (!_.findWhere(unref(currentOperatorsReference), { id: operatorId })) {
                const reservedOperator = _.findWhere(unref(reservedOperators), { id: operatorId });
                if (reservedOperator) {
                    reservedOperatorsReferenceValue.push(reservedOperator);
                }
            }
        });
        return reservedOperatorsReferenceValue;
    });

    const operatorsReference = computed(() => ([] as Operator[]).concat(unref(currentOperatorsReference), unref(reservedOperatorsReference)));

    const selectedOperatorsText = computed(() => {
        const selectedOperatorsValue = _.uniq(unref(selectedOperators).map((id) => canonicalOperatorId(id)));
        if (selectedOperatorsValue.length === 1) {
            const selectedOperator = unref(operatorsReference).find((operator) => canonicalOperatorId(operator.id) === selectedOperatorsValue[0]);
            return selectedOperator ? (selectedOperator.nameRu || selectedOperator.name) : undefined;
        }
        if (selectedOperatorsValue.length > 1) {
            return `${selectedOperatorsValue.length} туроператор${wordEnding(selectedOperatorsValue.length, ';а;ов')}`;
        }
        return 'Все туроператоры';
    });

    const moreThanOneOperatorAvailableOrSelected = computed(() => unref(operatorsReference).length > 1 || unref(selectedOperators).length);

    const isHotelOffer = computed(() => unref(productType) === 'hotelOffer');

    const allOperatorsChecked = computed({
        get: () => !unref(selectedOperators).length,
        set: (value) => {
            if (value) {
                selectedOperators.value = [];
            }
        },
    });

    return {
        selectedOperatorsText,
        operatorsReference,
        moreThanOneOperatorAvailableOrSelected,
        isHotelOffer,
        allOperatorsChecked,
    };
};
